import React, { useCallback } from 'react';
import {
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from 'react-plaid-link';
import { useDispatch } from 'react-redux';

import PaymentsClient from 'src/clients/PaymentsClient';
import {
  PaymentMethodOption,
  PaymentMethodOptionProps,
} from 'src/legacy/components/AddPaymentMethod/PaymentMethodOption';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { alertSnackbar } from 'src/store/ui/actions';

interface PlaidPaymentMethodOptionProps extends PaymentMethodOptionProps {
  token: string;
  receivedRedirectUri?: string;
  isMobileView?: boolean;
  previewMode?: boolean;
}

export const PlaidPaymentMethodOption =
  React.memo<PlaidPaymentMethodOptionProps>(
    ({
      token,
      receivedRedirectUri,
      onPaymentMethodOptionSelected,
      isMobileView,
      previewMode,
      ...other
    }) => {
      const dispatch = useDispatch();

      const [isBankSaving, setIsBankSaving] = React.useState(false);

      /**
       * when plaid flow is successfully completed, send the publicToken and accountId
       * to the server to save bank account info for customer
       */
      const handlePlaidLinkSuccess = useCallback<PlaidLinkOnSuccess>(
        async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
          setIsBankSaving(true);
          try {
            const bankSource = await PaymentsClient.savePlaidBankAccount(
              publicToken,
              metadata.accounts[0].id,
            );
            onPaymentMethodOptionSelected(bankSource);
          } catch (err) {
            console.error('Failed to save bank via plaid', err);
            dispatch(
              alertSnackbar({
                errorMessage:
                  'Your bank could not be connected, please contact support for help.',
              }),
            );
          }
          setIsBankSaving(false);
        },
        [],
      );

      const onExit = useCallback((error: any, metadata: any) => {
        console.info('Plaid on exit', error, metadata);
      }, []);

      const plaidConfig = {
        onSuccess: handlePlaidLinkSuccess,
        onExit,
        token,
        receivedRedirectUri,
      };

      const { ready, open } = usePlaidLink(plaidConfig);

      const handlePlaidSelected = () => {
        open();
      };

      React.useEffect(() => {
        if (ready && receivedRedirectUri) {
          open();
        }
      }, [ready, receivedRedirectUri]);

      return (
        <LoadingWrapper isLoading={isBankSaving}>
          <PaymentMethodOption
            {...other}
            onPaymentMethodOptionSelected={handlePlaidSelected}
            isMobileView={isMobileView}
            previewMode={previewMode}
          />
        </LoadingWrapper>
      );
    },
  );
